import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Box, Divider, Stack } from "@mui/material";
import AiChatHistory from "../components/AiChat/AiChatHistory";
import { useCase } from "./CaseProvider";
import AiChatThreadMessages from "../components/AiChat/AiChatThreadMessages";
import AIChatInput from "../components/AiChat/AIChatInput";
import { getAiAgentByUser, sendAiChatPrompt } from "../services/aiChatService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource } from "../services/ReactQuery/reactQueryService";
import { useAiEventStreamControls } from "../hooks/useAiEventStream";
import CollapsableWindow, { CollapsableWindowProps } from "../components/CollapsableWindow/CollapsableWindow";
import { useCurrentUser } from "./CurrentUserProvider";
import FloatingAgentButton from "../components/AiChat/FloatingAgentButton";
import AiChatToolbar from "../components/AiChat/AiChatToolbar";
import { AiChatAgent } from "../types/AIChat/AiChatThread";
import { useDrawer } from "./DrawerProvider";
import { DrawerState } from "../types/DrawerState";
import { AiChatMessage } from "../types/AIChat/AiChatMessage";
import { AiChatFeedbackRating } from "../types/AIChat/AiChatFeedback";
import AiChatFeedbackSheet from "../components/AiChat/AiChatFeedbackSheet";
import useAiChatReasoningStatus from "../hooks/useAiChatReasoningStatus.ts";
import { Helmet } from "react-helmet-async";
import { getCaseLabel } from "../services/Product/ProductService/productService.ts";

type AiChatProviderData = {
  openAiChat: (initialMessage: string) => void;
  isOpen: boolean;
  isLoading: boolean;
  setActiveThreadId: React.Dispatch<React.SetStateAction<number | null>>;
  setHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  activeThreadId: number | null;
  resetAiChat: (closeWindow?: boolean) => void;
  activeAgent: AiChatAgent;
  //execution step currently being edited in a drawer; the chat treats prompts as edit instructions for it
  activeEditStepId: number | null;
  openStepEditDrawer: (stepId: number, stepDrawerState: DrawerState) => void;
  closeStepEditDrawer: () => void;
  openFeedback: (message: AiChatMessage, rating: AiChatFeedbackRating) => void;
  editMessage: (message: AiChatMessage, content: string) => Promise<void>;
};

type AiChatFeedbackTarget = {
  message: AiChatMessage;
  rating: AiChatFeedbackRating;
};

const AiChatProviderContext = createContext<AiChatProviderData | null>(null);

export const AiChatProvider = ({ children }: { children: ReactNode }) => {
  const { product } = useCase();
  if (!product) {
    return null;
  }
  return <AiChatProviderInner key={"aiChat" + product.id}>{children}</AiChatProviderInner>;
};

const AiChatProviderInner = ({ children }: { children: ReactNode }) => {
  const [windowState, setWindowState] = useState<CollapsableWindowProps["windowState"]>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<number | null>(null);
  const [triggerAiChatReasoningStatusRead, setTriggerAiChatReasoningStatusRead] = useState<number>(0);
  const chatWrapperRef = useRef<HTMLDivElement>(null);
  const aiInputRef = useRef<HTMLTextAreaElement>(null);
  const { product } = useCase();
  const { setIndicatorType, indicatorType } = useAiChatReasoningStatus(triggerAiChatReasoningStatusRead);
  const currentUser = useCurrentUser();
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const queryClient = useQueryClient();
  const { connect, reset } = useAiEventStreamControls();
  const drawer = useDrawer();
  const [activeEditStepId, setActiveEditStepId] = useState<number | null>(null);
  const [feedbackTarget, setFeedbackTarget] = useState<AiChatFeedbackTarget | null>(null);

  useEffect(() => {
    const manageHelmet = () => {
      if (document.visibilityState === "visible") {
        setIndicatorType(null);
      } else {
        setTriggerAiChatReasoningStatusRead((prevState) => ++prevState);
      }
    };
    document.addEventListener("visibilitychange", manageHelmet);
    return () => {
      document.removeEventListener("visibilitychange", manageHelmet);
    };
  }, []);

  const closeFeedback = () => setFeedbackTarget(null);

  const setHistoryOpenExtended: AiChatProviderData["setHistoryOpen"] = (nextState) => {
    const nextHistoryOpen = typeof nextState === "function" ? nextState(historyOpen) : nextState;
    if (nextHistoryOpen) {
      closeFeedback();
    }
    setHistoryOpen(nextHistoryOpen);
  };

  const openFeedback = (message: AiChatMessage, rating: AiChatFeedbackRating) => {
    setHistoryOpen(false);
    setFeedbackTarget({ message, rating });
  };

  // The drawer's onClose clears activeEditStepId on every close or replace path, so the id cannot go stale.
  const openStepEditDrawer = (stepId: number, stepDrawerState: DrawerState) => {
    drawer.setDrawerState({ ...stepDrawerState, onClose: () => setActiveEditStepId(null) });
    setActiveEditStepId(stepId);
  };

  // This closes the step drawer before the chat closes or changes threads, so it cannot show old data.
  // It also lets useDraftAutosave save pending changes when the form is removed.
  const closeStepEditDrawer = () => {
    if (activeEditStepId !== null) {
      drawer.closeDrawer();
    }
  };

  const changeActiveThread: AiChatProviderData["setActiveThreadId"] = (nextThreadId) => {
    closeStepEditDrawer();
    closeFeedback();
    setActiveThreadId(nextThreadId);
  };

  const activeAgent = getAiAgentByUser(currentUser);

  const resetAiChat = (closeWindow = true) => {
    changeActiveThread(null);
    setHistoryOpenExtended(false);
    setIsLoading(false);
    if (closeWindow) {
      setWindowState(null);
    }
  };

  if (!product) {
    return null;
  }

  if (!activeAgent) {
    return children;
  }

  const openAiChat = async (initialMessage?: string) => {
    setWindowState("maximized");
    if (initialMessage) {
      await sendAiChatPrompt({
        product,
        activeThreadId: null,
        setActiveThreadId: changeActiveThread,
        createMutation,
        queryClient,
        connect,
        reset,
        prompt: initialMessage,
        setIsLoading,
        agentType: activeAgent,
      });
    }
  };

  const editMessage = async (message: AiChatMessage, content: string) => {
    if (activeThreadId === null) {
      throw new Error("An active AI chat thread is required to edit a message.");
    }

    await sendAiChatPrompt({
      product,
      activeThreadId,
      setActiveThreadId: changeActiveThread,
      createMutation,
      queryClient,
      connect,
      reset,
      prompt: content,
      setIsLoading,
      agentType: activeAgent,
      editedMessageUuid: message.uuid,
    });
  };

  const data: AiChatProviderData = {
    openAiChat,
    isOpen: ["maximized", "minimized"].includes(windowState || ""),
    setActiveThreadId: changeActiveThread,
    activeThreadId,
    setHistoryOpen: setHistoryOpenExtended,
    resetAiChat,
    setIsLoading,
    isLoading,
    activeAgent,
    activeEditStepId,
    openStepEditDrawer,
    closeStepEditDrawer,
    openFeedback,
    editMessage,
  };

  return (
    <AiChatProviderContext.Provider value={data}>
      {document.visibilityState === "hidden" && indicatorType && (
        <Helmet>
          <title>{`
          ${indicatorType === "loading" ? "⏳" : ""}
          ${indicatorType === "alert" ? "🔔 Antwort bereit | " : ""}
          ${getCaseLabel(product)} | Legalbird`}</title>
        </Helmet>
      )}
      <CollapsableWindow windowState={windowState} elementToFocusRef={aiInputRef}>
        <AiChatToolbar windowState={windowState} setWindowState={setWindowState} />
        <Stack ref={chatWrapperRef} sx={{ flexGrow: 1, position: "relative", overflow: "hidden" }}>
          <Box sx={{ flexGrow: 1, padding: "1rem", overflowY: "auto" }}>
            <AiChatThreadMessages />
          </Box>
          <Divider />
          {/* Keyed by thread: switching threads deliberately resets the input — the typed draft
              is discarded and a running voice session is cancelled via unmount. */}
          <AIChatInput key={activeThreadId ?? "new"} ref={aiInputRef} />
          {feedbackTarget && activeThreadId && (
            <AiChatFeedbackSheet
              key={`${feedbackTarget.message.id}:${feedbackTarget.rating}`}
              message={feedbackTarget.message}
              rating={feedbackTarget.rating}
              activeThreadId={activeThreadId}
              chatWrapperRef={chatWrapperRef}
              onClose={closeFeedback}
            />
          )}
          <AiChatHistory
            open={historyOpen}
            closeHistory={() => setHistoryOpenExtended(false)}
            chatWrapperRef={chatWrapperRef}
            backofficeCaseIri={product.backofficeCase["@id"]}
          />
        </Stack>
      </CollapsableWindow>
      <Box sx={{ position: "fixed", bottom: "3rem", right: "10rem", zIndex: 100 }}>
        {!windowState && <FloatingAgentButton disabled={!!windowState} onClick={() => setWindowState("maximized")} />}
      </Box>
      {children}
    </AiChatProviderContext.Provider>
  );
};

export const useAiChat = () => {
  const context = useContext(AiChatProviderContext);
  if (!context) {
    throw new Error("useAiChat must be used within a AiChatProvider");
  }
  return context;
};

export const useOptionalAiChat = () => {
  return useContext(AiChatProviderContext) || null;
};
